import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListCadencesResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button, EmptyState, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-73 Cadences (F-055): a vertical list of steps with the wait between them
 * as a chip, "stop when they reply or book a meeting" at the top and on by
 * default, and where each enrolled person is.
 */

type Cadence = ListCadencesResult['cadences'][number];
type Step = Cadence['steps'][number];
const CHANNELS: Step['channel'][] = ['email', 'call', 'linkedin', 'task'];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';
const blank = (): Step => ({ channel: 'email', delayDays: 0, subject: '', body: '' });

function CadencesScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const list = useQuery<ListCadencesResult, ApiError>({ queryKey: ['cadences'], queryFn: () => api.listCadences({}) });
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [name, setName] = useState('');
  const [active, setActive] = useState(false);
  const [exitOnReply, setExitOnReply] = useState(true);
  const [steps, setSteps] = useState<Step[]>([blank()]);
  const current = list.data?.cadences.find((c) => c.cadenceId === selected) ?? null;
  useEffect(() => {
    if (selected === 'new') {
      setName('');
      setActive(false);
      setExitOnReply(true);
      setSteps([blank()]);
    } else if (current) {
      setName(current.name);
      setActive(current.active);
      setExitOnReply(current.exitOnReply);
      setSteps(current.steps);
    }
  }, [selected, current]);
  const save = useMutation({
    mutationFn: () =>
      api.saveCadence({ body: { cadenceId: selected === 'new' ? null : selected, name, active, exitOnReply, enrolRule: current?.enrolRule ?? null, steps } }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['cadences'] });
      setSelected(saved.cadenceId);
    },
  });
  const exit = useMutation({
    mutationFn: (enrolmentId: string) => api.exitCadenceEnrolment({ path: { enrolmentId }, body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cadences'] }),
  });
  if (list.isPending) return <Skeleton className="h-plot w-full" />;
  if (list.isError) return <ErrorState title={t('state.error')} body={errorText(list.error)} onRetry={() => void list.refetch()} />;
  const patch = (index: number, change: Partial<Step>) => setSteps((all) => all.map((s, i) => (i === index ? { ...s, ...change } : s)));
  const manage = can('automation.manage');

  return (
    <div data-component="Cadences" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{t('cadence.title')}</PageHeading>
        {manage ? (
          <Button intent="secondary" size="small" data-cadence-new className="ms-auto" onClick={() => setSelected('new')}>
            {t('cadence.new')}
          </Button>
        ) : null}
      </header>
      <div className="flex flex-col gap-4 lg:flex-row">
        <Panel className="lg:w-panel-narrow">
          {list.data.cadences.length === 0 ? (
            <p className="text-meta text-ink-meta">{t('cadence.none')}</p>
          ) : (
            <ul className="flex flex-col">
              {list.data.cadences.map((cadence) => (
                <li key={cadence.cadenceId}>
                  <button type="button" data-cadence={cadence.name} aria-current={selected === cadence.cadenceId ? 'true' : undefined} onClick={() => setSelected(cadence.cadenceId)} className="w-full rounded-control px-2 py-1 text-start text-dense text-ink hover:bg-canvas aria-[current]:bg-bronze-tint">
                    {cadence.name} <span className="text-meta text-ink-meta">{cadence.active ? t('cadence.on') : t('cadence.off')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        {selected ? (
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <Panel data-region="cadence-builder">
              <div className="flex flex-col gap-3">
                <input data-cadence-name aria-label={t('cadence.name')} placeholder={t('cadence.name')} className={INPUT} value={name} onChange={(e) => setName(e.target.value)} disabled={!manage} />
                <label data-cadence-exit className="flex items-center gap-2 rounded-control bg-success-tint px-3 py-2 text-dense text-ink">
                  <input type="checkbox" data-cadence-exit-on-reply checked={exitOnReply} onChange={(e) => setExitOnReply(e.target.checked)} disabled={!manage} />
                  {t('cadence.exitOnReply')}
                </label>
                {!exitOnReply ? <p data-cadence-exit-warning className="text-dense text-danger-text">{t('cadence.exitWarning')}</p> : null}
                <ol className="flex flex-col gap-2">
                  {steps.map((step, index) => (
                    <li key={index} data-cadence-step={index + 1} className="flex flex-col gap-2">
                      {index > 0 || step.delayDays > 0 ? (
                        <span data-delay-chip className="self-start rounded-chip bg-sunken px-2 text-meta text-ink-600">
                          {t('cadence.wait', { values: { days: String(step.delayDays) } })}
                        </span>
                      ) : null}
                      <div className="flex flex-col gap-2 rounded-card border border-rule p-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-dense text-ink">{String(index + 1)}.</span>
                          <select data-step-channel aria-label={t('cadence.channel')} className={INPUT} value={step.channel} onChange={(e) => patch(index, { channel: e.target.value as Step['channel'] })} disabled={!manage}>
                            {CHANNELS.map((channel) => (
                              <option key={channel} value={channel}>{t(`cadence.channel.${channel}`)}</option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1 text-meta text-ink-600">
                            {t('cadence.after')}
                            <input data-step-delay inputMode="numeric" className={`${INPUT} w-16`} value={String(step.delayDays)} onChange={(e) => patch(index, { delayDays: Number.parseInt(e.target.value, 10) || 0 })} disabled={!manage} />
                            {t('cadence.days')}
                          </label>
                          {steps.length > 1 && manage ? (
                            <Button intent="ghost" size="small" className="ms-auto" onClick={() => setSteps((all) => all.filter((_, i) => i !== index))}>
                              {t('cadence.removeStep')}
                            </Button>
                          ) : null}
                        </div>
                        {step.channel === 'email' ? (
                          <input data-step-subject aria-label={t('cadence.subject')} placeholder={t('cadence.subject')} className={INPUT} value={step.subject ?? ''} onChange={(e) => patch(index, { subject: e.target.value })} disabled={!manage} />
                        ) : null}
                        <textarea data-step-body rows={3} aria-label={t('cadence.words')} placeholder={step.channel === 'email' ? t('cadence.emailHint') : t('cadence.taskHint')} className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink" value={step.body} onChange={(e) => patch(index, { body: e.target.value })} disabled={!manage} />
                      </div>
                    </li>
                  ))}
                </ol>
                {manage ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button intent="ghost" size="small" data-cadence-add-step onClick={() => setSteps((all) => [...all, { ...blank(), delayDays: 3 }])}>
                      {t('cadence.addStep')}
                    </Button>
                    <label className="flex items-center gap-1 text-dense text-ink">
                      <input type="checkbox" data-cadence-active checked={active} onChange={(e) => setActive(e.target.checked)} />
                      {t('cadence.switchOn')}
                    </label>
                    <Button intent="primary" size="small" data-cadence-save disabled={save.isPending} onClick={() => save.mutate()}>
                      {t('cadence.save')}
                    </Button>
                  </div>
                ) : null}
                {save.error ? <p data-cadence-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
              </div>
            </Panel>
            {current ? (
              <Panel data-region="cadence-enrolled">
                <div className="flex flex-col gap-1">
                  <SectionLabel>{t('cadence.enrolled')}</SectionLabel>
                  {current.enrolled.length === 0 ? <p className="text-meta text-ink-meta">{t('cadence.nobody')}</p> : null}
                  <ul className="flex flex-col">
                    {current.enrolled.map((person) => (
                      <li key={person.enrolmentId} data-enrolled={person.name} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                        <span className="text-dense text-ink">{person.name}</span>
                        <span className="text-meta text-ink-meta">
                          {person.status === 'active'
                            ? t('cadence.atStep', { values: { step: String(person.currentStep), at: person.nextDueAt ? formatDateTime(person.nextDueAt, format) : '-' } })
                            : `${t(`cadence.status.${person.status as 'completed'}`)}${person.exitReason ? ` - ${person.exitReason}` : ''}`}
                        </span>
                        {person.status === 'active' && manage ? (
                          <Button intent="ghost" size="small" className="ms-auto" onClick={() => exit.mutate(person.enrolmentId)}>
                            {t('cadence.takeOut')}
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </Panel>
            ) : null}
          </div>
        ) : (
          <EmptyState reason="no-records" title={t('cadence.pick')} body={t('cadence.pickBody')} primary={manage ? { label: t('cadence.new'), href: '/cadences', icon: 'Plus' } : undefined} />
        )}
      </div>
    </div>
  );
}

function Loading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function Failed(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('cadence.none')} />;
}

export const screen = defineScreen({
  id: 'SC-73',
  route: '/cadences',
  surface: 'page',
  title: 'Cadences',
  features: ['F-055'],
  entitlement: 'F-055',
  reachedBy: [{ kind: 'nav', item: 'cadences' }],
  components: [],
  elements: ['Cadences'],
  states: { empty: Loading, loading: Loading, error: Failed },
  component: CadencesScreen,
});

export default screen;
